import React, { useCallback, useEffect, useState } from "react";

import { getFullFarmDetails, linkDealToFarm, toggleTest, updateDealById } from "services";

import { useAdminUserContext, useCancelAsyncOnUnmount, useLocation, useParams } from "utils";

import { AdminHead, ErrorBoundary, MainPage, Modal, Tabs, TagNormal, Tooltip, Message as message } from "components";

import { FarmAdminTab } from "pages/admin/farms/components/FarmAdminTab";
import { FarmDetailsTab } from "pages/admin/farms/components/FarmDetailsTab";
import { FarmFieldsTab } from "pages/admin/farms/components/FarmFieldsTab";
import { FarmPointsTab } from "pages/admin/farms/components/FarmPointsTab";
import { RelatedFarmsTab } from "pages/admin/farms/components/RelatedFarmsTab";

export const Farm = () => {
  const { id } = useParams();
  const { user } = useAdminUserContext();

  const location = useLocation();

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [dealId, setDealId] = useState(null);
  const [updatingDeal, setUpdatingDeal] = useState(false);
  const [farm, setFarm] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [coordinates, setCoordinates] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isTest, setIsTest] = useState(false);
  const signal = useCancelAsyncOnUnmount()();

  const loadFarm = useCallback(() => {
    setLoading(true);

    getFullFarmDetails({
      id: id,
    })
      .then((res) => {
        const markers = [];

        res.fieldList.forEach((item) => {
          if (item.location) {
            markers.push({
              location: item.location,
              name: item.name,
              id: item.id,
              size: Math.sqrt((parseFloat(item.size) * 10000) / Math.PI),
            });
            setCoordinates(item.location);
          }
        });

        setMarkers(markers);
        setFarm(res);
        setSaving(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    setIsTest(farm.test);
  }, [farm]);

  const handleTestChecked = (checked) => {
    setIsTest(checked);

    toggleTest({ farmId: farm.id }).then(() => {
      // Reload farm to have updated data
      // This will also reset the Checkbox if the toggle call
      // was unsuccessful
      getFullFarmDetails({
        id: id,
        signal,
      }).then((res) => {
        setFarm(res);
      });
    });
  };

  const updateFarmDeal = () => {
    setUpdatingDeal(true);
    updateDealById({
      dealId: farm.dealId,
      farm,
      signal,
    }).then((res) => {
      setUpdatingDeal(false);
      if (res) {
        message({
          content: "The germlins updated the deal!",
          icon: "gremlin",
        });
      }
    });
  };
  const linkDeal = () => {
    setUpdatingDeal(true);
    linkDealToFarm({
      dealId,
      farm,
      signal,
    }).then((res) => {
      setUpdatingDeal(false);
      setShowModal(false);
      setUpdatingDeal(false);
      if (res) {
        loadFarm();
        message({
          content: "The germlins linked the deal to this farm!",
          icon: "gremlin",
        });
      }
    });
  };

  useEffect(() => {
    loadFarm();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const params = new URLSearchParams(location.search);
  const defaultTab = params.get("activeTab") || "";

  return (
    <MainPage loading={loading} pageName={"admin"}>
      <AdminHead
        headline={
          <>
            {farm.name}
            {farm.verified ? (
              <Tooltip placement="bottom" title="This farm is verified">
                <span>
                  <TagNormal color="green" text="Verified" style={{ transform: "translate3d(0, -50%, 0) scale(0.9)" }} />
                </span>
              </Tooltip>
            ) : (
              <Tooltip placement="bottom" title="This farm is not verified">
                <span>
                  <TagNormal color="red" text="Unverified" style={{ transform: "translate3d(0, -50%, 0) scale(0.9)" }} />
                </span>
              </Tooltip>
            )}
          </>
        }
        link="/farms/farms"
        tooltipText="Back to farms"
      />
      {!loading && (
        <div className="content">
          <Tabs
            defaultActiveKey={defaultTab || "farm"}
            items={[
              {
                label: "Farm details",
                key: "farm",
                children: (
                  <ErrorBoundary>
                    <FarmDetailsTab farm={farm} />
                  </ErrorBoundary>
                ),
              },
              {
                label: "Fields",
                key: "fields",
                children: (
                  <ErrorBoundary>
                    <FarmFieldsTab farm={farm} loadFarm={loadFarm} location={coordinates} markers={markers} user={user} saving={saving} />
                  </ErrorBoundary>
                ),
              },
              {
                label: "Points",
                key: "points",
                children: (
                  <ErrorBoundary>
                    <FarmPointsTab farm={farm} />
                  </ErrorBoundary>
                ),
              },
              {
                label: "Related farms",
                key: "related-farms",
                children: (
                  <ErrorBoundary>
                    <RelatedFarmsTab currentFarm={farm} />
                  </ErrorBoundary>
                ),
              },
              {
                label: "Farm admin",
                key: "farm-admin",
                children: (
                  <ErrorBoundary>
                    <FarmAdminTab
                      farm={farm}
                      isTest={isTest}
                      updateFarmDeal={updateFarmDeal}
                      updatingDeal={updatingDeal}
                      setShowModal={setShowModal}
                      handleTestChecked={handleTestChecked}
                      loadFarm={loadFarm}
                      saving={saving}
                    />
                  </ErrorBoundary>
                ),
              },
            ]}
          />
          <Modal
            cancelText={"close"}
            description={
              <>
                <div>Open the corresponding deal in pipedrive and copy the correct ID from the page url, found in: https://klim2.pipedrive.com/deal/ID. </div>
              </>
            }
            input={{
              initialValue: "",
              name: "dealId",
              rules: [{ required: true }],
              label: "dealId",
              onChange: (v) => setDealId(v.target.value),
            }}
            inputType="text"
            onCancel={() => {
              setShowModal(false);
              setDealId(null);
              setUpdatingDeal(false);
            }}
            onOk={() => linkDeal()}
            okText={"link"}
            showModal={showModal}
            title={"Link deal to farm"}
          />
        </div>
      )}
    </MainPage>
  );
};
